import React, { useState } from "react";
import type { Invoice } from "../../../hooks/useMyAccountData";
import { fetchInvoiceDocument } from "../../../services/invoiceService";

interface InvoicesModalProps {
  invoices: Invoice[];
  loading?: boolean;
  onClose: () => void;
}

const InvoicesModal: React.FC<InvoicesModalProps> = ({ invoices, loading, onClose }) => {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadErrors, setDownloadErrors] = useState<Record<string, string>>({});

  const handleTaxInvoiceDownload = async (invoice: Invoice) => {
    // AL-326: gated on taxInvoiceAvailable, which the backend sets for every succeeded payment, and
    // keyed on the payment rather than on an invoice number. The previous guard required an existing
    // /^\d{9,10}$/ number, which no payment had until its first invoice was issued — and issuing one
    // is what this call does. That was unsatisfiable by construction.
    if (!invoice.taxInvoiceAvailable || downloadingId) return;

    setDownloadingId(invoice.id);
    setDownloadErrors((current) => ({ ...current, [invoice.id]: "" }));

    // Open during the user gesture so browsers do not block the async download.
    const downloadWindow = window.open("", "_blank");
    if (downloadWindow) downloadWindow.opener = null;

    try {
      const invoiceDocument = await fetchInvoiceDocument(invoice.id);
      if (downloadWindow) {
        downloadWindow.location.href = invoiceDocument.url;
      } else {
        const link = document.createElement("a");
        link.href = invoiceDocument.url;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.click();
      }
    } catch (error) {
      downloadWindow?.close();
      setDownloadErrors((current) => ({
        ...current,
        [invoice.id]: error instanceof Error
          ? error.message
          : "Unable to download the tax invoice. Please try again.",
      }));
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div
      className="modal-overlay"
      data-testid="invoices-modal-overlay"
      onClick={onClose}
    >
      <div
        className="modal-content large"
        data-testid="invoices-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header" data-testid="invoices-modal-header">
          <h3>Invoices</h3>
          <button
            className="close-button"
            data-testid="invoices-modal-close-button"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        <div className="modal-body" data-testid="invoices-modal-body">
          <table className="invoices-table" data-testid="invoices-table">
            <thead>
              <tr>
                <th>Invoice Number</th>
                <th>Date</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && invoices.length === 0 && (
                <tr data-testid="invoices-loading-row">
                  <td colSpan={5} style={{ textAlign: "center", color: "#64748b" }}>
                    Loading invoices from ARCaml…
                  </td>
                </tr>
              )}
              {!loading && invoices.length === 0 && (
                <tr data-testid="invoices-empty-row">
                  <td colSpan={5} style={{ textAlign: "center", color: "#64748b" }}>
                    No invoices found.
                  </td>
                </tr>
              )}
              {invoices.map((invoice) => {
                // AL-326: show whatever number we hold, whatever its shape. One derived value used to
                // drive both this column and the download button, so a number failing a format test
                // vanished from the row entirely and the customer saw an internal payment id in a
                // column headed "Invoice ID". Display and availability are separate questions.
                const invoiceNumber = invoice.invoiceNumber?.trim() || undefined;
                const isDownloading = downloadingId === invoice.id;

                return (
                  <tr key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                    <td data-testid={`invoice-id-${invoice.id}`}>
                      {invoiceNumber || invoice.id}
                    </td>
                    <td data-testid={`invoice-date-${invoice.id}`}>
                      {invoice.date}
                    </td>
                    <td data-testid={`invoice-amount-${invoice.id}`}>
                      {invoice.amount}
                    </td>
                    <td>
                      <span
                        className={`status-badge ${invoice.status}`}
                        data-testid={`invoice-status-${invoice.id}`}
                      >
                        {invoice.status.toUpperCase()}
                      </span>
                    </td>
                    <td>
                      {invoice.taxInvoiceAvailable ? (
                        <>
                          <button
                            type="button"
                            className="link-button"
                            data-testid={`invoice-download-${invoice.id}`}
                            disabled={isDownloading}
                            onClick={() => handleTaxInvoiceDownload(invoice)}
                            style={{ border: 0, background: "none", padding: 0 }}
                          >
                            {isDownloading ? "Fetching from ARCaml…" : "Download Tax Invoice"}
                          </button>
                          <div
                            style={{ color: "#64748b", fontSize: 10, marginTop: 3 }}
                          >
                            Generated by ARCaml
                          </div>
                          {downloadErrors[invoice.id] && (
                            <div
                              role="alert"
                              style={{ color: "#b91c1c", fontSize: 12, marginTop: 4 }}
                            >
                              {downloadErrors[invoice.id]}
                            </div>
                          )}
                        </>
                      ) : invoice.pdfUrl ? (
                        <a
                          className="link-button"
                          data-testid={`invoice-download-${invoice.id}`}
                          href={invoice.pdfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Invoice
                        </a>
                      ) : (
                        <span
                          className="link-button disabled"
                          data-testid={`invoice-download-${invoice.id}`}
                          title="A tax invoice is not available for this payment."
                        >
                          Invoice unavailable
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="modal-footer" data-testid="invoices-modal-footer">
          <button
            className="action-button primary"
            data-testid="invoices-modal-close-button-footer"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoicesModal;
